import React from "react";
import { api } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { TextInput, TextArea, toast } from "@/components/admin/ui";
import { announceLiveSiteBaseUrl, LIVE_SITE_DEFAULT } from "@/lib/admin/liveSite";

export default function Settings() {
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [pw, setPw] = React.useState({ current: "", next1: "", next2: "" });
  const [pwBusy, setPwBusy] = React.useState(false);
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => { api.getContent().then(setForm); }, []);

  // Load launch-checklist data
  React.useEffect(() => {
    (async () => {
      try {
        const [products, testimonials, categories] = await Promise.all([
          api.listProducts(false).catch(() => []),
          api.listTestimonials(false).catch(() => []),
          api.getCategories().catch(() => []),
        ]);
        setStats({ products, testimonials, categories });
      } catch (e) { console.warn("[Settings] stats load failed:", e); }
    })();
  }, []);

  if (!form) return <div className="text-[#6e6f5f] text-sm p-8">Loading…</div>;

  const prizes = Array.isArray(form.popup_prizes) ? form.popup_prizes : [];
  const wheelPrizes = Array.isArray(form.wheel_prizes) ? form.wheel_prizes : [];
  function patch(p) { setForm((f) => ({ ...f, ...p })); setDirty(true); }
  function patchWheelPrize(i, k, v) {
    const next = wheelPrizes.map((p, j) => j === i ? { ...p, [k]: v } : p);
    patch({ wheel_prizes: next });
  }
  function addWheelPrize() {
    patch({ wheel_prizes: [...wheelPrizes, { id: `prize${wheelPrizes.length + 1}`, label: "New reward", sub: "", weight: 10 }] });
  }
  function rmWheelPrize(i) {
    if (wheelPrizes.length <= 2) {
      toast("Keep at least 2 prizes on the wheel", "error");
      return;
    }
    patch({ wheel_prizes: wheelPrizes.filter((_, j) => j !== i) });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        announcement_enabled: !!form.announcement_enabled,
        announcement_text: form.announcement_text || "",
        // Reward wheel: master toggle + campaign id (lets each visitor spin once per campaign)
        reward_wheel_enabled: !!form.reward_wheel_enabled,
        reward_wheel_campaign_id: form.reward_wheel_campaign_id || "v1",
        wheel_prizes: Array.isArray(form.wheel_prizes) ? form.wheel_prizes : [],
        popup_enabled: !!form.popup_enabled,
        popup_title: form.popup_title || "",
        popup_subtitle: form.popup_subtitle || "",
        popup_p: form.popup_p || "",
        popup_prizes: Array.isArray(form.popup_prizes) ? form.popup_prizes : [],
        live_site_base_url: (form.live_site_base_url || "").trim() || LIVE_SITE_DEFAULT,
      };
      const next = await api.saveContent(payload);
      setForm((f) => ({ ...f, ...next }));
      announceLiveSiteBaseUrl(payload.live_site_base_url);
      setDirty(false);
      toast("Settings saved");
    } catch (e) { toast(e.response?.data?.detail || "Save failed", "error"); }
    finally { setSaving(false); }
  }

  async function resetWheel() {
    if (!window.confirm("Allow EVERY visitor to spin the wheel one more time?\n\nThis lets returning customers spin again.")) return;
    const newId = "v" + (Date.now()).toString(36);
    try {
      await api.saveContent({ reward_wheel_campaign_id: newId });
      setForm((f) => ({ ...f, reward_wheel_campaign_id: newId }));
      toast("Wheel reset — every visitor can spin once more");
    } catch (e) { toast("Failed to reset", "error"); }
  }

  async function resetPopup() {
    if (!window.confirm("Re-show the bonus popup to every visitor (including people who've already seen it)?")) return;
    const v = (form.popup_version || 1) + 1;
    try {
      await api.saveContent({ popup_version: v });
      setForm((f) => ({ ...f, popup_version: v }));
      toast("Popup reset — every visitor will see it again");
    } catch (e) { toast("Failed to reset", "error"); }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (pw.next1 !== pw.next2) return toast("New passwords don't match", "error");
    if (pw.next1.length < 8) return toast("Password must be at least 8 characters", "error");
    setPwBusy(true);
    try {
      await api.changePassword(pw.current, pw.next1);
      toast("Password updated");
      setPw({ current: "", next1: "", next2: "" });
    } catch (ex) { toast(ex.response?.data?.detail || "Failed to update password", "error"); }
    finally { setPwBusy(false); }
  }

  function setPrize(i, v) { patch({ popup_prizes: prizes.map((p, j) => j === i ? v : p) }); }
  function addPrize() { patch({ popup_prizes: [...prizes, "New reward"] }); }
  function rmPrize(i) { patch({ popup_prizes: prizes.filter((_, j) => j !== i) }); }

  return (
    <div data-testid="settings-page" className="-mx-4 lg:-mx-8 -my-4 lg:-my-8 min-h-screen px-4 lg:px-8 py-4 lg:py-8 pb-32">
      <PageHead eye="SETTINGS" title="Site Settings" sub="Announcement banner, bonus popup, and admin password." />

      <div className="space-y-5 max-w-4xl">
        {/* §0 Launch Checklist */}
        <LaunchChecklist form={form} stats={stats} />

        {/* §A Announcement */}
        <Card letter="A" title="Site Announcement"
              right={<ToggleRow data-testid="settings-announce-toggle" checked={!!form.announcement_enabled} onChange={(v) => patch({ announcement_enabled: v })} label={form.announcement_enabled ? "Showing" : "Hidden"} />}>
          <Field label="Banner text" hint="Thin strip across the top of the public site when enabled.">
            <TextInput data-testid="settings-announce-text" value={form.announcement_text || ""} onChange={(e) => patch({ announcement_text: e.target.value })} placeholder="e.g. Fresh drop landing Friday · Members only" />
          </Field>
        </Card>

        {/* §B Reward wheel */}
        <Card letter="B" title="Reward Wheel"
              right={<ToggleRow data-testid="settings-wheel-enable" checked={!!form.reward_wheel_enabled} onChange={(v) => patch({ reward_wheel_enabled: v })} label={form.reward_wheel_enabled ? "Enabled" : "Disabled"} />}>
          <div className="text-[11.5px] text-[#a9aa9a] leading-relaxed">
            Each visitor sees the wheel <b className="text-[#c9a86b]">only once</b> (locked by browser).
            When you click <b className="text-[#c9a86b]">Reset</b>, every visitor — old and new — gets one fresh chance to spin.
          </div>
          <div className="text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] mt-2">
            Current campaign: <span className="text-[#c9a86b]">{form.reward_wheel_campaign_id || "v1"}</span>
          </div>
          <div className="pt-3 border-t border-[#22261b]">
            <button
              data-testid="settings-wheel-reset"
              onClick={resetWheel}
              className="px-4 py-2 rounded-md text-[10.5px] tracking-[.2em] uppercase font-bold border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08]"
            >↻ Reset all spins</button>
          </div>
        </Card>

        {/* §B2 Reward Wheel Prizes — what members can win */}
        <Card letter="B+" title="Reward Wheel Prizes">
          <div className="text-[11.5px] text-[#a9aa9a] leading-relaxed">
            Edit what members can actually win when they spin the wheel.
            Each row is one slice — set a name, a one-line description, and
            the <b className="text-[#c9a86b]">odds weight</b> (higher = more likely).
            Slices are auto-arranged on the wheel by order.
          </div>
          <div className="space-y-2.5">
            {wheelPrizes.map((p, i) => {
              const totalWeight = wheelPrizes.reduce((s, x) => s + (Number(x.weight) || 0), 0) || 1;
              const pct = Math.round(((Number(p.weight) || 0) / totalWeight) * 100);
              return (
                <div
                  key={p.id || `slot-${i}`}
                  data-testid={`wheel-prize-row-${i}`}
                  className="rounded-lg border border-[#22261b] bg-black/20 p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-end"
                >
                  <div className="md:col-span-1 text-center">
                    <div className="text-[9px] tracking-[.18em] uppercase text-[#6e6f5f] mb-1">Slot</div>
                    <div className="text-[18px] font-bold text-[#c9a86b] tabular-nums">{i + 1}</div>
                  </div>
                  <Field className="md:col-span-4" label="Prize name">
                    <TextInput
                      data-testid={`wheel-prize-label-${i}`}
                      value={p.label || ""}
                      onChange={(e) => patchWheelPrize(i, "label", e.target.value)}
                      placeholder="e.g. Free Pre-roll"
                    />
                  </Field>
                  <Field className="md:col-span-4" label="One-line description">
                    <TextInput
                      data-testid={`wheel-prize-sub-${i}`}
                      value={p.sub || ""}
                      onChange={(e) => patchWheelPrize(i, "sub", e.target.value)}
                      placeholder="e.g. Bonus on next order"
                    />
                  </Field>
                  <Field className="md:col-span-2" label={`Odds (${pct}%)`}>
                    <TextInput
                      data-testid={`wheel-prize-weight-${i}`}
                      type="number"
                      min="1"
                      max="100"
                      value={p.weight ?? 10}
                      onChange={(e) => patchWheelPrize(i, "weight", parseInt(e.target.value, 10) || 1)}
                    />
                  </Field>
                  <div className="md:col-span-1 flex md:justify-end">
                    <button
                      data-testid={`wheel-prize-remove-${i}`}
                      onClick={() => rmWheelPrize(i)}
                      className="px-3 py-2 text-[10px] tracking-[.18em] uppercase text-red-300 border border-red-500/30 rounded-md hover:bg-red-500/10"
                    >Del</button>
                  </div>
                </div>
              );
            })}
            <button
              data-testid="wheel-prize-add"
              onClick={addWheelPrize}
              className="w-full px-3 py-2.5 text-[10px] tracking-[.18em] uppercase text-[#c9a86b] border border-dashed border-[#c9a86b]/30 rounded-md hover:bg-[#c9a86b]/[.08]"
            >+ Add reward slice</button>
          </div>
          <div className="text-[10.5px] text-[#6e6f5f] leading-relaxed pt-3 border-t border-[#22261b]">
            <b className="text-[#a9aa9a]">Tip:</b> Recommended 5–6 prizes. Higher odds-weight = appears more often.
            After saving, click <b className="text-[#c9a86b]">"Reset all spins"</b> above so every visitor sees the new wheel.
          </div>
        </Card>

        {/* §C Bonus popup (legacy) */}
        <Card letter="C" title="Bonus Popup (Legacy / Optional)"
              right={<ToggleRow data-testid="settings-popup-enable" checked={!!form.popup_enabled} onChange={(v) => patch({ popup_enabled: v })} label={form.popup_enabled ? "Enabled" : "Disabled"} />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title">
              <TextInput data-testid="settings-popup-title" value={form.popup_title || ""} onChange={(e) => patch({ popup_title: e.target.value })} />
            </Field>
            <Field label="Subtitle">
              <TextInput data-testid="settings-popup-sub" value={form.popup_subtitle || ""} onChange={(e) => patch({ popup_subtitle: e.target.value })} />
            </Field>
          </div>
          <Field label="Paragraph">
            <TextArea data-testid="settings-popup-p" rows={3} value={form.popup_p || ""} onChange={(e) => patch({ popup_p: e.target.value })} />
          </Field>
          <Field label="Reward options (wheel has 6 slices)">
            <div className="space-y-2">
              {prizes.map((prize, i) => (
                <div key={`popup-prize-${i}`} className="flex gap-2">
                  <TextInput data-testid={`settings-prize-${i}`} value={prize} onChange={(e) => setPrize(i, e.target.value)} />
                  <button onClick={() => rmPrize(i)} className="px-3 text-[10px] tracking-[.18em] uppercase text-red-300 border border-red-500/30 rounded-md hover:bg-red-500/10">Remove</button>
                </div>
              ))}
              <button data-testid="settings-prize-add" onClick={addPrize} className="px-3 py-2 text-[10px] tracking-[.18em] uppercase text-[#c9a86b] border border-[#c9a86b]/30 rounded-md hover:bg-[#c9a86b]/[.08]">+ Add reward</button>
            </div>
          </Field>
          <div className="pt-3 border-t border-[#22261b]">
            <button
              data-testid="settings-popup-reset"
              onClick={resetPopup}
              className="px-4 py-2 rounded-md text-[10.5px] tracking-[.2em] uppercase font-bold border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08]"
            >↻ Reset popup for everyone</button>
          </div>
        </Card>

        {/* §D Live Frontend Site */}
        <Card letter="D" title="Live Frontend Site URL">
          <Field
            label="Base URL of the public site"
            hint='Used by "View on Live Site" buttons and by cms-bridge.js when rewriting hero CTAs. Include trailing slash. Example: https://royalblue-squid-369670.hostingersite.com/2High/site/'
          >
            <TextInput
              data-testid="settings-live-site"
              type="url"
              placeholder={LIVE_SITE_DEFAULT}
              value={form.live_site_base_url ?? ""}
              onChange={(e) => patch({ live_site_base_url: e.target.value })}
            />
          </Field>
          <div className="flex items-center gap-2">
            <button
              data-testid="settings-live-site-reset"
              onClick={() => patch({ live_site_base_url: LIVE_SITE_DEFAULT })}
              className="px-3 py-1.5 rounded-md border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] text-[10.5px] tracking-[.2em] uppercase"
            >Restore default</button>
            {form.live_site_base_url && (
              <a
                data-testid="settings-live-site-open"
                href={form.live_site_base_url}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded-md border border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] text-[10.5px] tracking-[.2em] uppercase"
              >Open ↗</a>
            )}
          </div>
        </Card>

        {/* §E Backup & Restore — one-click site migration */}
        <BackupRestoreCard />

        {/* §F Password */}
        <Card letter="F" title="Change Admin Password">
          <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Current"><TextInput data-testid="settings-pw-current" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required /></Field>
            <Field label="New (min 8)"><TextInput data-testid="settings-pw-new1" type="password" value={pw.next1} onChange={(e) => setPw({ ...pw, next1: e.target.value })} required /></Field>
            <Field label="Confirm new"><TextInput data-testid="settings-pw-new2" type="password" value={pw.next2} onChange={(e) => setPw({ ...pw, next2: e.target.value })} required /></Field>
            <div className="md:col-span-3">
              <button
                type="submit"
                data-testid="settings-pw-submit"
                disabled={pwBusy}
                className="px-5 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold disabled:opacity-50"
                style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
              >{pwBusy ? "Updating…" : "Update Password"}</button>
            </div>
          </form>
        </Card>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:left-[240px] px-5 py-3 bg-[#10120c]/95 backdrop-blur border-t border-[#22261b] flex items-center justify-between gap-3">
        <div className="text-[11px] font-mono tracking-[.06em] text-[#6e6f5f]">
          {dirty ? <span className="text-[#c9a86b]">● Unsaved changes</span> : <span className="text-[#5fa15a]">● All saved</span>}
        </div>
        <button
          data-testid="settings-save"
          onClick={save}
          disabled={saving || !dirty}
          className="px-5 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold disabled:opacity-50"
          style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
        >{saving ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
}

function Card({ letter, title, right, children }) {
  return (
    <section className="rounded-2xl border border-[#22261b] bg-[#13150f] p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-[22px] h-[22px] rounded-md grid place-items-center text-[12px] font-bold" style={{ background: "#c9a86b", color: "#1a1407" }}>{letter}</span>
          <h3 className="text-[12.5px] tracking-[.16em] uppercase text-[#a9aa9a] m-0">{title}</h3>
        </div>
        {right}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[10.5px] text-[#6e6f5f] mt-1.5">{hint}</span>}
    </label>
  );
}

function ToggleRow({ label, checked, onChange, "data-testid": testid }) {
  return (
    <label data-testid={testid} className="flex items-center gap-2 cursor-pointer">
      <span className={`relative w-9 h-5 rounded-full border transition ${checked ? "bg-[#2d3a23] border-[#5fa15a]" : "bg-[#1f2218] border-[#2c3122]"}`}>
        <span className={`absolute top-[1px] w-3.5 h-3.5 rounded-full transition-all ${checked ? "left-[18px] bg-[#5fa15a]" : "left-[2px] bg-[#6e6f5f]"}`} />
      </span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="hidden" />
      <span className="text-[12px] text-[#a9aa9a]">{label}</span>
    </label>
  );
}

/* ============================== LAUNCH CHECKLIST ============================== */
function LaunchChecklist({ form, stats }) {
  if (!stats) {
    return (
      <section className="rounded-2xl border border-[#c9a86b]/35 bg-gradient-to-br from-[#1a1810]/90 to-[#13150f] p-5">
        <div className="text-[11px] text-[#a9aa9a]">Loading launch readiness…</div>
      </section>
    );
  }
  const { products = [], testimonials = [], categories = [] } = stats;
  const pub = products.filter(p => p.published !== false);
  const featured = pub.filter(p => p.show_on_homepage === true);
  const tgConnected = !!(form.contact_telegram_url || form.contact_telegram_handle);

  // Compute the 8 checks
  const items = [
    {
      id: "tg",
      label: "Telegram link connected",
      hint: "Set your @official2high handle in Contact Links",
      ok: tgConnected,
      cta: { href: "#/owner-cabinet/contact", label: "Set link" },
    },
    {
      id: "products",
      label: "At least 6 published products",
      hint: `You have ${pub.length} live${pub.length < 6 ? ` · add ${6 - pub.length} more` : ""}`,
      ok: pub.length >= 6,
      cta: { href: "#/owner-cabinet", label: "Add product" },
    },
    {
      id: "featured",
      label: "4 products featured on homepage",
      hint: `${featured.length}/4 starred · empty slots show "Coming Soon"`,
      ok: featured.length >= 4,
      cta: { href: "#/owner-cabinet", label: "Star drops" },
    },
    {
      id: "proof",
      label: "≥ 3 social-proof items",
      hint: `${testimonials.length} testimonials/landed photos`,
      ok: testimonials.length >= 3,
      cta: { href: "#/owner-cabinet/proof", label: "Add proof" },
    },
    {
      id: "promo",
      label: "Promo video uploaded",
      hint: form.founder_video ? "Video set ✓" : "Upload your 30-60s intro video",
      ok: !!form.founder_video,
      cta: { href: "#/owner-cabinet/homepage", label: "Upload video" },
    },
    {
      id: "categories",
      label: "Categories reviewed & ordered",
      hint: `${categories.filter(c => c.visible !== false).length} visible · drag to reorder`,
      ok: categories.length > 0,
      cta: { href: "#/owner-cabinet/categories", label: "Review" },
    },
    {
      id: "password",
      label: "Admin password changed from default",
      hint: "Change from Admin888 to something only you know",
      ok: !!form._pw_changed,
      cta: { href: "#section-password", label: "Change ↓" },
    },
    {
      id: "domain",
      label: "Custom domain connected",
      hint: form.custom_domain ? `Pointing to ${form.custom_domain}` : "Currently on preview URL · Save to Deploy in chat",
      ok: !!form.custom_domain,
      cta: { href: "#deploy-help", label: "How to" },
    },
  ];

  const doneCount = items.filter(i => i.ok).length;
  const pct = Math.round((doneCount / items.length) * 100);

  return (
    <section data-testid="launch-checklist" className="rounded-2xl border border-[#c9a86b]/35 bg-gradient-to-br from-[#1a1810]/90 to-[#13150f] p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-[22px] h-[22px] rounded-md grid place-items-center text-[12px] font-bold" style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}>0</span>
          <div>
            <h3 className="text-[13px] tracking-[.16em] uppercase text-[#e8e4d8] m-0 font-semibold">Launch Readiness</h3>
            <div className="text-[10.5px] text-[#6e6f5f] tracking-[.1em] uppercase mt-0.5">Complete these 8 items to feel production-ready</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[26px] font-bold leading-none tabular-nums" style={{ color: pct === 100 ? "#5fa15a" : "#c9a86b" }}>{pct}%</div>
          <div className="text-[10px] tracking-[.16em] uppercase text-[#6e6f5f] mt-0.5">{doneCount} / {items.length}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-1.5 rounded-full bg-[#1f2218] mb-4 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 transition-all"
          style={{ width: `${pct}%`, background: pct === 100 ? "linear-gradient(90deg,#5fa15a,#82c478)" : "linear-gradient(90deg,#a07c39,#d4b675)" }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`launch-${it.id}`}
            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border ${it.ok ? "border-[#5fa15a]/30 bg-[#5fa15a]/[0.06]" : "border-[#22261b] bg-black/20"}`}
          >
            <span
              className={`shrink-0 mt-0.5 w-4 h-4 rounded-full grid place-items-center text-[10px] font-bold ${it.ok ? "bg-[#5fa15a] text-[#0c1408]" : "border border-[#3a3f2e] text-[#6e6f5f]"}`}
            >{it.ok ? "✓" : ""}</span>
            <div className="min-w-0 flex-1">
              <div className={`text-[12px] font-medium leading-tight ${it.ok ? "text-[#a8d39d]" : "text-[#e8e4d8]"}`}>{it.label}</div>
              <div className="text-[10.5px] text-[#6e6f5f] mt-0.5 leading-snug">{it.hint}</div>
            </div>
            {!it.ok && (
              <a
                href={it.cta.href.replace(/^#/, "")}
                className="shrink-0 text-[9.5px] tracking-[.18em] uppercase font-mono text-[#c9a86b] hover:text-[#e2c790] whitespace-nowrap"
              >{it.cta.label} →</a>
            )}
          </div>
        ))}
      </div>

      {pct === 100 && (
        <div className="mt-4 px-4 py-3 rounded-lg bg-[#5fa15a]/[0.1] border border-[#5fa15a]/40 text-[12px] text-[#a8d39d]">
          🚀 <b>Ready to launch.</b> Use the "Save to Deploy" feature in the Emergent chat to publish your custom domain.
        </div>
      )}
    </section>
  );
}

/* ============================== BACKUP & RESTORE ============================== */
function BackupRestoreCard() {
  const [busy, setBusy] = React.useState(null); // 'export' | 'restore' | null
  const [lastExport, setLastExport] = React.useState(null);
  const [lastRestore, setLastRestore] = React.useState(null);
  const fileRef = React.useRef(null);

  async function handleExport() {
    setBusy("export");
    try {
      const data = await api.exportBackup();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `2high-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLastExport({ at: data.exported_at, counts: data.counts });
      toast("Backup downloaded");
    } catch (e) {
      toast(e.response?.data?.detail || "Backup failed", "error");
    } finally {
      setBusy(null);
    }
  }

  function pickFile() {
    fileRef.current?.click();
  }

  async function handleRestoreFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      toast("Could not read the file — is it a valid JSON backup?", "error");
      return;
    }
    if (!parsed?.collections) {
      toast("File doesn't look like a 2High backup (no 'collections' key)", "error");
      return;
    }
    const counts = parsed.counts || {};
    const summary = Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(", ") || "data";
    if (!window.confirm(
      `Restore this backup?\n\nWill REPLACE ALL current data with:\n${summary}\n\nExported: ${parsed.exported_at || "unknown"}\n\nThis cannot be undone. Continue?`
    )) return;
    setBusy("restore");
    try {
      const res = await api.restoreBackup({ ...parsed, mode: "replace" });
      setLastRestore({ at: new Date().toISOString(), restored: res.restored, skipped: res.skipped });
      toast("Backup restored — refresh to see changes");
    } catch (e) {
      toast(e.response?.data?.detail || "Restore failed", "error");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section data-testid="backup-restore-card" className="rounded-2xl border border-[#22261b] bg-[#13150f] p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-[22px] h-[22px] rounded-md grid place-items-center text-[12px] font-bold" style={{ background: "#c9a86b", color: "#1a1407" }}>E</span>
          <h3 className="text-[12.5px] tracking-[.16em] uppercase text-[#a9aa9a] m-0">Backup & Restore</h3>
        </div>
      </div>

      <div className="text-[11.5px] text-[#a9aa9a] leading-relaxed">
        Download a full JSON snapshot of every editable collection — products, testimonials,
        landed packages, categories, draft orders, wheel prizes, inquiries, and content edits.
        Use it to migrate to a fresh deployment, or as a disaster-recovery point. Restore
        replaces all current data with the backup file.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          data-testid="backup-export-btn"
          onClick={handleExport}
          disabled={busy !== null}
          className="px-4 py-3 rounded-lg text-[12px] tracking-[.18em] uppercase font-bold border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy === "export" ? "Exporting…" : "↓ Download Backup (JSON)"}
        </button>
        <button
          data-testid="backup-restore-btn"
          onClick={pickFile}
          disabled={busy !== null}
          className="px-4 py-3 rounded-lg text-[12px] tracking-[.18em] uppercase font-bold border border-red-500/40 text-red-300 hover:bg-red-500/[.08] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy === "restore" ? "Restoring…" : "↑ Restore from Backup"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={handleRestoreFile}
          className="hidden"
          data-testid="backup-restore-file"
        />
      </div>

      {(lastExport || lastRestore) && (
        <div className="space-y-1.5 pt-3 border-t border-[#22261b] text-[10.5px] text-[#6e6f5f]">
          {lastExport && (
            <div data-testid="backup-last-export">
              <b className="text-[#a9aa9a]">Last export:</b>{" "}
              {Object.entries(lastExport.counts).map(([k, v]) => `${v} ${k}`).join(" · ")}
            </div>
          )}
          {lastRestore && (
            <div data-testid="backup-last-restore">
              <b className="text-[#a9aa9a]">Last restore:</b>{" "}
              {Object.entries(lastRestore.restored).map(([k, v]) => `${v} ${k}`).join(" · ")}
              {lastRestore.skipped?.length ? ` · Skipped: ${lastRestore.skipped.join(", ")}` : ""}
            </div>
          )}
        </div>
      )}

      <div className="text-[10.5px] text-[#6e6f5f] leading-relaxed pt-2 border-t border-[#22261b]">
        <b className="text-[#a9aa9a]">⚠ Note on uploaded media:</b> images & videos are stored on
        the server filesystem. The JSON backup contains the URLs but not the files themselves.
        After restoring on a new deployment, re-upload any custom images that were on the
        previous server (or set up a CDN like S3 for true portability).
      </div>
    </section>
  );
}

